if (!customElements.get('lumera-external-buy')) {
  customElements.define('lumera-external-buy', class extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('product-info');
      this.form = this.scope?.querySelector(`[id="${this.dataset.formId}"]`);
      this.native = this.form?.closest('product-form');
      this.select = this.querySelector('[data-lumera-supply]');
      this.link = this.querySelector('[data-lumera-buy]');
      this.status = this.querySelector('[data-lumera-status]');
      this.summary = this.querySelector('[data-lumera-selection]');
      this.retry = this.querySelector('[data-lumera-retry]');
      if (!this.scope || !this.form || !this.native || !this.select || !this.link) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      try {
        this.links = JSON.parse(this.querySelector('[data-lumera-links]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-lumera-variants]').textContent);
        this.copy = JSON.parse(this.querySelector('[data-lumera-copy]').textContent);
        if (!this.links || !Array.isArray(this.variants)) throw new Error('Invalid product data');
      } catch {
        this.links = {};
        this.variants = [];
        this.copy = { error: this.dataset.errorMessage || '' };
        this.invalid = true;
      }
      this.scope.addEventListener('product-info:variant-pending', () => this.setState('pending'), options);
      this.scope.addEventListener('product-info:variant-error', () => this.setState('error'), options);
      this.scope.addEventListener('product-info:variant-ready', (event) => this.update(event.detail.variant?.id), options);
      this.select.addEventListener('change', () => {
        if (this.state === 'ready') this.update(this.variantId);
      }, options);
      this.retry?.addEventListener('click', () => this.scope.retryVariantRequest?.(), options);
      this.scope.addEventListener('submit', (event) => {
        if (event.target !== this.form) return;
        if (this.state !== 'ready' || this.url) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.state === 'ready' && this.url) window.location.assign(this.url);
        }
      }, { capture: true, signal: this.controller.signal });
      this.link.addEventListener('click', (event) => {
        if (this.state !== 'ready' || !this.url) event.preventDefault();
      }, options);
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        if (this.state !== 'ready' && this.scope.lastVariantRequest) {
          this.scope.retryVariantRequest?.();
          return;
        }
        this.update(this.form.querySelector('input[name="id"]')?.value);
      }, options);
      this.update(this.form.querySelector('input[name="id"]')?.value || this.dataset.variantId);
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
    validUrl(candidate) {
      if (typeof candidate !== 'string' || !candidate.trim()) return '';
      try {
        const parsed = new URL(candidate.trim());
        return parsed.protocol === 'https:' && parsed.hostname === 'trinitycellscience.patient.oladigital.health'
          && !parsed.username && !parsed.password && !parsed.port ? candidate.trim() : '';
      } catch {
        return '';
      }
    }
    normalizeOptions(picker, target) {
      if (!this.variants?.length) return;
      const groups = [...picker.querySelectorAll('.product-form__input')];
      const index = groups.findIndex((group) => group.contains(target));
      if (index < 0) return;
      const values = groups.map((group) => group.querySelector('select')?.value || group.querySelector('input:checked')?.value);
      const matches = this.variants.filter((variant) => variant.available && values.slice(0, index + 1).every((value, i) => variant.options[i] === value));
      const variant = matches.find((item) => item.options.every((value, i) => value === values[i])) || matches[0];
      if (!variant) return;
      groups.forEach((group, i) => {
        const select = group.querySelector('select');
        if (select) {
          select.value = variant.options[i];
          picker.updateSelectionMetadata({ target: select });
        } else {
          const input = [...group.querySelectorAll('input[type="radio"]')].find((item) => item.value === variant.options[i]);
          if (input) {
            input.checked = true;
            picker.updateSelectionMetadata({ target: input });
          }
        }
      });
    }
    updateOptionAvailability() {
      const groups = [...this.scope.querySelectorAll('variant-selects:not([style*="display: none"]) .product-form__input')];
      const selected = groups.map((group) => group.querySelector('select')?.value || group.querySelector('input:checked')?.value);
      groups.forEach((group, index) => {
        group.querySelectorAll('option, input[type="radio"]').forEach((option) => {
          option.disabled = !this.variants.some((variant) => variant.available && variant.options[index] === option.value && selected.slice(0, index).every((value, i) => value === variant.options[i]));
        });
      });
    }
    update(variantId) {
      this.variantId = String(variantId || '');
      this.variant = this.variants.find((variant) => String(variant.id) === this.variantId);
      const supplies = this.links[this.variantId];
      if (this.invalid || !this.variant || !supplies || typeof supplies !== 'object') {
        this.setState(this.invalid ? 'error' : 'unavailable');
        return;
      }
      const hasMapping = Object.values(supplies).some((value) => Boolean(value));
      const urls = { '1': this.validUrl(supplies['1']), '3': this.validUrl(supplies['3']) };
      if (hasMapping && !urls['1']) {
        this.setState('error');
        return;
      }
      const previousSupply = this.select.value;
      for (const option of this.select.options) option.disabled = !urls[option.value];
      if (!urls[this.select.value]) this.select.value = '1';
      this.url = urls[this.select.value] || '';
      this.mapped = hasMapping;
      this.updateOptionAvailability();
      this.summary.textContent = this.variant.title === 'Default Title' ? '' : this.variant.title;
      this.setState(this.variant.available ? 'ready' : 'unavailable', previousSupply === '3' && this.select.value === '1' && hasMapping ? this.copy.supply_reset : '');
    }
    setState(state, message = '') {
      this.state = state;
      const ready = state === 'ready';
      this.hidden = ready && !this.mapped;
      this.native.hidden = !this.hidden;
      this.scope.toggleAttribute('data-lumera-external', !this.hidden);
      this.select.disabled = !ready;
      this.toggleAttribute('aria-busy', state === 'pending');
      this.link.removeAttribute('href');
      this.link.setAttribute('aria-disabled', 'true');
      if (ready && this.url) {
        this.link.href = this.url;
        this.link.removeAttribute('aria-disabled');
      }
      if (!ready) this.native.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');
      if (this.retry) this.retry.hidden = state !== 'error' || !this.scope.lastVariantRequest;
      if (this.status) this.status.textContent = message || (ready ? '' : this.copy[state] || this.copy.error);
      this.scope.dispatchEvent(new CustomEvent('lumera:selection', { bubbles: true, detail: {
        productId: this.scope.dataset.productId,
        ready, external: this.mapped, url: ready ? this.url : '',
        summary: ready ? [this.summary.textContent, this.mapped ? this.select.selectedOptions[0]?.textContent : ''].filter(Boolean).join(' · ') : this.status?.textContent,
        label: this.link.textContent.trim()
      } }));
    }
  });
}

if (!customElements.get('lumera-sticky-selection')) {
  customElements.define('lumera-sticky-selection', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.link = this.querySelector('[data-lumera-sticky-buy]');
      this.edit = this.querySelector('[data-lumera-edit]');
      this.summary = this.querySelector('[data-lumera-sticky-summary]');
      this.main = document.querySelector(`product-info[id^="MainProduct-"][data-product-id="${this.dataset.productId}"]`);
      this.edit.addEventListener('click', () => {
        const input = this.main?.querySelector('variant-selects select, variant-selects input:checked, [data-lumera-supply]');
        input?.scrollIntoView({ block: 'center', behavior: 'auto' });
        input?.focus({ preventScroll: true });
      }, options);
      this.link.addEventListener('click', (event) => {
        if (!this.link.hasAttribute('href')) {
          event.preventDefault();
          if (this.selection?.ready && !this.selection.external) {
            const form = this.main?.querySelector('product-form form');
            const submit = form?.querySelector('[type="submit"]');
            if (submit && !submit.disabled) form.requestSubmit(submit);
          }
        }
      }, options);
      this.link.addEventListener('keydown', (event) => {
        if (!this.link.hasAttribute('href') && ['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.link.click();
        }
      }, options);
      document.addEventListener('lumera:selection', (event) => {
        if (event.target !== this.main) return;
        this.selection = event.detail;
        this.summary.textContent = event.detail.summary;
        this.link.textContent = event.detail.external ? event.detail.label : this.dataset.nativeLabel;
        this.link.removeAttribute('href');
        this.link.setAttribute('role', event.detail.external ? 'link' : 'button');
        this.link.setAttribute('aria-disabled', String(!event.detail.ready));
        if (event.detail.url) this.link.href = event.detail.url;
      }, options);
      const purchase = this.main?.querySelector('lumera-external-buy');
      if (purchase?.state) purchase.setState(purchase.state);
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}
